import { useMutation } from '@tanstack/react-query';
import {
  addServiceToRealm,
  AddServiceToRealmErrorResponse,
  AddServiceToRealmPayload,
  AddServiceToRealmSuccessResponse,
} from 'RealmModule/services/addServiceToRealmApi';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useNotification } from 'hooks/useNotification';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import ApiClientManager from 'core/apiClient';

export const useAddServiceToRealm = ({
  onSuccess,
}: {
  /**
   * Called after the service is successfully added. Registered at the
   * mutation (not observer) level so it still fires when the calling
   * component unmounted while the request was in flight (e.g. the modal
   * embedding the form was closed) — per-call `mutate` callbacks would be
   * dropped in that case.
   */
  onSuccess?: VoidFunction;
} = {}) => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation<
    AxiosResponse<AddServiceToRealmSuccessResponse>,
    AxiosResponse<AddServiceToRealmErrorResponse>,
    AddServiceToRealmPayload
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      return addServiceToRealm({
        params,
        token,
        // Route the request against the target realm — without explicit
        // headers, x-tenant/x-realm-id fall back to the logged-in admin's
        // current context, which can differ from the realm in the payload
        // (e.g. adding from the Create Draft modal of a team in another
        // tenant).
        config: {
          headers: ApiClientManager.buildUrlHeaders({
            tenantName: params.tenant,
            realmId: params.realmId,
          }),
        },
      }) as Promise<AxiosResponse<AddServiceToRealmSuccessResponse>>;
    },
    onSuccess: () => {
      // ✅ FIX: Replace alert() with toast notification
      showSuccess('Add service to realm successful!');
      onSuccess?.();
    },
    onError: (error) => {
      // ✅ FIX: Replace alert() with toast notification
      showError(`Add service to realm failed! ${extractApiErrorMessage(error)}`);
    },
  });
  return {
    addServiceToRealm: mutation.mutate,
    isAddingServiceToRealm: mutation.isPending,
  };
};
